import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';

import AdvertiserPagesService, {
  AdvertiserPage,
  AdvertiserPageGroup,
} from 'services/AdvertiserPagesService';
import StaticAdsService from 'services/StaticAdsService';
import { getActorName } from 'shared/hooks/useActorName';

import {
  FOLLOW_STATE,
  FollowedBrandStatus,
  followedBrandStatus,
  STALE_AFTER_HOURS,
  timeOf,
} from './followedBrandStatus';
import {
  FOLLOW_OPTION,
  FollowOption,
  followOptionKey,
  followOptionLabel,
  followOptions,
} from './followOptions';

// Brands whose static ads are fetched every morning (the collector's
// ci-static-followed-brands job): each page's own Ad Library listing, top ads by
// reach, new ones added to the Library unanalyzed. A brand is an advertiser
// group, made under "Advertiser pages"; following is a flag on that group. A
// single page is followed through a one-page group of its own (followOptions.ts).

function ago(value: string | null): string {
  const ms = timeOf(value);
  if (ms == null) return 'never';
  const hours = (Date.now() - ms) / 3600000;
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min ago`;
  if (hours < 48) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function StatusChip({ status }: { status: FollowedBrandStatus }) {
  switch (status.state) {
    case FOLLOW_STATE.ERROR:
      return (
        <Tooltip
          title={status.errors
            .map(e => `${e.pageName}: ${e.message}`)
            .join(' · ')}
        >
          <Chip size="small" color="error" label="Last check failed" />
        </Tooltip>
      );
    case FOLLOW_STATE.STALE:
      return (
        <Tooltip
          title={`Not checked in over ${STALE_AFTER_HOURS} hours: the morning check did not run for at least one page.`}
        >
          <Chip size="small" color="warning" label="Missed a check" />
        </Tooltip>
      );
    case FOLLOW_STATE.WAITING:
      return (
        <Chip size="small" variant="outlined" label="Waiting for first check" />
      );
    default:
      return (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label="Up to date"
        />
      );
  }
}

/** `version` changes when advertiser pages or groups were edited elsewhere on the tab. */
export default function FollowedBrands({ version }: { version: number }) {
  const pagesService = useMemo(() => new AdvertiserPagesService(), []);
  const staticService = useMemo(() => new StaticAdsService(), []);

  const [groups, setGroups] = useState<AdvertiserPageGroup[]>([]);
  const [pages, setPages] = useState<AdvertiserPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // groupId (or pageId) being changed
  const [toFollow, setToFollow] = useState<FollowOption | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [g, p] = await Promise.all([
      pagesService.listGroups(true),
      pagesService.list(true),
    ]);
    if (g && p) {
      setGroups(g.groups);
      setPages(p.pages);
      setError(null);
    } else setError('Could not load followed brands.');
    setLoading(false);
  }, [pagesService]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const followed = groups.filter(g => g.followStatics);
  const options = useMemo(() => followOptions(groups, pages), [groups, pages]);

  const setFollow = async (
    group: AdvertiserPageGroup,
    followStatics: boolean,
  ) => {
    setBusy(group.groupId);
    setError(null);
    setNotice(null);
    try {
      await pagesService.updateGroup(group.groupId, { followStatics });
      setToFollow(null);
      // Before the first check, so a failure there still shows the saved follow.
      await load();
      if (followStatics) {
        // Fetch right away rather than leaving a new brand empty until the morning.
        const { queued } = await staticService.fetchFollowedBrand(
          group.groupId,
        );
        setNotice(
          `Following ${group.groupName}. Checking its ${queued} page${queued === 1 ? '' : 's'} now; new ads appear in the Library within a few minutes.`,
        );
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Could not update the brand',
      );
    } finally {
      setBusy(null);
    }
  };

  const followSelected = async (option: FollowOption) => {
    if (option.kind === FOLLOW_OPTION.GROUP) {
      await setFollow(option.group, true);
      return;
    }
    // A page: give it a one-page group named after it, then follow that. The
    // group shows under Advertiser pages → Groups like any other.
    setBusy(option.page.pageId);
    setError(null);
    setNotice(null);
    let group: AdvertiserPageGroup;
    try {
      ({ group } = await pagesService.createGroup({
        groupName: option.page.pageName,
        pageIds: [option.page.pageId],
        createdBy: getActorName() || undefined,
      }));
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Could not follow the page',
      );
      setBusy(null);
      return;
    }
    // If the follow below fails, a retry follows this group, not a second one.
    setGroups(gs => [...gs, group]);
    setToFollow({ kind: FOLLOW_OPTION.GROUP, group });
    await setFollow(group, true);
  };

  const checkNow = async (group: AdvertiserPageGroup) => {
    setBusy(group.groupId);
    setError(null);
    setNotice(null);
    try {
      const { queued } = await staticService.fetchFollowedBrand(group.groupId);
      setNotice(
        `Checking ${group.groupName}'s ${queued} page${queued === 1 ? '' : 's'} now. Refresh in a few minutes to see the result.`,
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Could not start the check',
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        spacing={2}
        sx={{ mb: 1.5 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Followed brands
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every morning at 5am PT, each followed brand&apos;s top 30 static
            ads by reach are checked and any new ones are added to the Library,
            unanalyzed. Follow a single advertiser page, or a brand: a group of
            pages, made under Advertiser pages.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Autocomplete
            size="small"
            sx={{ width: 280 }}
            options={options}
            value={toFollow}
            onChange={(_, value) => setToFollow(value)}
            groupBy={o => (o.kind === FOLLOW_OPTION.GROUP ? 'Brands' : 'Pages')}
            getOptionLabel={followOptionLabel}
            isOptionEqualToValue={(a, b) =>
              followOptionKey(a) === followOptionKey(b)
            }
            noOptionsText="Every page and brand is followed. Add one under Advertiser pages."
            renderInput={params => (
              <TextField {...params} label="Page or brand to follow" />
            )}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!toFollow || !!busy}
            onClick={() => toFollow && followSelected(toFollow)}
          >
            Follow
          </Button>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                aria-label="Refresh followed brands"
                onClick={() => void load()}
                disabled={loading}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !followed.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No brands followed yet. Pick one above to have its new static ads
          added every morning.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Brand</TableCell>
              <TableCell align="center">Pages</TableCell>
              <TableCell>Last checked</TableCell>
              <TableCell align="center">New ads (last check)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {followed.map(group => {
              const status = followedBrandStatus(group, pages);
              return (
                <TableRow key={group.groupId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {group.groupName}
                    </Typography>
                    {group.notes && (
                      <Typography variant="caption" color="text.secondary">
                        {group.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{status.pageCount}</TableCell>
                  <TableCell>{ago(status.checkedAt)}</TableCell>
                  <TableCell align="center">
                    {status.state === FOLLOW_STATE.WAITING
                      ? '—'
                      : status.newAds}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={status} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <Button
                        size="small"
                        disabled={!!busy || !status.pageCount}
                        onClick={() => checkNow(group)}
                      >
                        {busy === group.groupId ? (
                          <CircularProgress size={14} />
                        ) : (
                          'Check now'
                        )}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        disabled={!!busy}
                        onClick={() => setFollow(group, false)}
                      >
                        Unfollow
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
